// UNIVAPLAT-1918: Admin WhatsApp message log view page.
import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {NgxDatatableModule} from '@swimlane/ngx-datatable';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {BreadcrumbService} from '@cdss/services/breadcrumb.service';
import {SpinnerComponent} from '@shared/components/spinner/spinner.component';
import {OverlaySpinnerComponent} from '@shared/components/overlay-spinner/overlay-spinner.component';
import {MaskedFieldComponent} from '@shared/components/masked-field/masked-field.component';
import {AdminWhatsappService} from '@shared/api/services/v2/admin-whatsapp.service';
import {WhatsAppMessage} from '@shared/api/models/query/admin-whatsapp.model';
import {AdminWhatsappNewMessageModal} from './modals/admin-whatsapp-new-message.modal';
import {AdminWhatsappConversationComponent} from './conversation/admin-whatsapp-conversation.component';

@Component({
    selector: 'cdss-admin-messaging-page',
    standalone: true,
    imports: [CommonModule, NgxDatatableModule, SpinnerComponent, OverlaySpinnerComponent, MaskedFieldComponent, RouterLink, AdminWhatsappConversationComponent],
    templateUrl: './admin-messaging.page.html',
    styleUrls: ['./admin-messaging.page.scss']
})
export class AdminMessagingPage implements OnInit {
    userId: number = 0;
    loading = true;
    messages: WhatsAppMessage[] = [];
    errorMessage: string | null = null;
    /**
     * Phone sourced from route state (passed by admin-dashboard on navigation).
     * Public because the page header shows it alongside the user id. Carries the phone
     * exactly as the backend supplies it — in clear while sensitive-data masking is
     * deactivated platform-wide (UNIVAPLAT-2791, Epic UNIVAPLAT-2776); masked when the
     * flag is on, with reveal handled by the table's `app-masked-field`.
     */
    maskedPhone: string = '';

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private adminWhatsappService: AdminWhatsappService,
        private breadcrumbService: BreadcrumbService,
        private modalService: NgbModal,
    ) {}

    ngOnInit(): void {
        this.userId = +this.route.snapshot.paramMap.get('userId')!;
        // Read phone from route state so "To:" is populated even on the first send.
        this.maskedPhone = this.router.getCurrentNavigation()?.extras?.state?.['maskedPhone']
            ?? (history.state as { maskedPhone?: string })?.maskedPhone
            ?? '';
        this.breadcrumbService.set([
            {title: 'Admin Dashboard', url: '/cdss/admin-dashboard'},
            {title: 'WhatsApp conversation', url: null}
        ]);
        this.loadMessages();
    }

    async loadMessages(): Promise<void> {
        this.loading = true;
        try {
            await this.refreshMessages();
        } finally {
            this.loading = false;
        }
    }

    /**
     * Re-fetches the message log WITHOUT toggling `loading` — used after a send from the
     * conversation panel (or the new-message modal) so the table updates in place instead of
     * blanking the whole page behind the top-level spinner (which is gated on `loading` and
     * wraps the entire two-column layout, including the conversation panel itself).
     */
    async refreshMessages(): Promise<void> {
        this.errorMessage = null;
        try {
            const response = await this.adminWhatsappService.getMessageLog(this.userId);
            this.messages = response.messages ?? [];
            // UNIVAPLAT-1953: precedence for "To" field population:
            // 1. history.state.maskedPhone (set in ngOnInit from dashboard navigation)
            // 2. response.targetUserMaskedPhone (new field — works even with zero history)
            // 3. messages[0].maskedPhone (legacy belt-and-braces, kept for existing data)
            if (!this.maskedPhone && response.targetUserMaskedPhone) {
                this.maskedPhone = response.targetUserMaskedPhone;
            }
            if (!this.maskedPhone && this.messages.length > 0 && this.messages[0].maskedPhone) {
                this.maskedPhone = this.messages[0].maskedPhone;
            }
        } catch (err: any) {
            this.errorMessage = err?.error?.message ?? err?.message ?? 'Failed to load message log.';
        }
    }

    openNewMessageModal(): void {
        const modalRef = this.modalService.open(AdminWhatsappNewMessageModal, {size: 'lg', centered: true});
        modalRef.componentInstance.userId = this.userId;
        modalRef.componentInstance.maskedPhone = this.maskedPhone;
        modalRef.result.then(result => {
            if (result === 'refresh') {
                this.refreshMessages();
            }
        }).catch(() => {/* dismissed */});
    }
}
